import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Bell, CalendarClock, FileStack, FileText, LayoutDashboard, LifeBuoy, Menu, Moon, ReceiptText, ShieldCheck, Sun, X,
} from 'lucide-react';
import { NotificationsPanel } from '@/components/layout/NotificationsPanel';
import { PortalProfileMenu } from '@/components/layout/PortalProfileMenu';
import { Footer } from '@/components/layout/Footer';
import { useTheme } from '@/hooks/useTheme';
import { useAuth } from '@/hooks/useAuth';
import logo from '@/assets/logo.png';

const NAV = [
  { to: '/portal', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/portal/projects', label: 'Projects', icon: FileStack },
  { to: '/portal/invoices', label: 'Invoices & Payments', icon: ReceiptText },
  { to: '/portal/meetings', label: 'Meetings', icon: CalendarClock },
  { to: '/portal/approvals', label: 'Approvals', icon: ShieldCheck },
  { to: '/portal/tickets', label: 'Support Tickets', icon: LifeBuoy },
  { to: '/portal/documents', label: 'Documents', icon: FileText },
  { to: '/portal/notifications', label: 'Notifications', icon: Bell },
];

/**
 * The Client Portal's own shell — deliberately not AppLayout. No
 * Sidebar (staff nav), no GlobalSearch (searches org-wide staff data),
 * no WorkspaceSwitcher. A client-role user never renders a single staff
 * component; the only shared pieces are NotificationsPanel (already
 * personal-scoped) and a portal-specific ProfileMenu variant.
 */
export function PortalLayout() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { organizationId } = useAuth();
  const { resolvedTheme, toggle } = useTheme();

  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-950">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 overflow-hidden border-r border-gray-100 bg-white dark:border-gray-800 dark:bg-gray-900 md:flex">
        <PortalNavContent />
      </aside>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="fixed inset-0 z-40 bg-black/40 md:hidden"
            />
            <motion.div
              initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }}
              transition={{ type: 'tween', duration: 0.2, ease: 'easeOut' }}
              className="fixed inset-y-0 left-0 z-50 w-72 bg-white shadow-2xl dark:bg-gray-900 md:hidden"
            >
              <div className="flex items-center justify-between border-b border-gray-100 p-3 dark:border-gray-800">
                <span className="px-2 text-sm font-semibold text-gray-900 dark:text-gray-100">Client Portal</span>
                <button type="button" onClick={() => setIsMobileMenuOpen(false)} aria-label="Close menu" className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="h-[calc(100%-53px)]" onClick={() => setIsMobileMenuOpen(false)}>
                <PortalNavContent />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-gray-100 bg-white/90 px-4 py-2.5 backdrop-blur-md dark:border-gray-800 dark:bg-gray-900/90 sm:px-6">
          <button type="button" onClick={() => setIsMobileMenuOpen(true)} aria-label="Open menu" className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 md:hidden">
            <Menu className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">Client Portal</p>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={toggle}
              aria-label="Toggle theme"
              className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100"
            >
              {resolvedTheme === 'dark' ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
            </button>
            <NotificationsPanel />
            <div className="mx-1 hidden h-6 w-px bg-gray-200 dark:bg-gray-800 sm:block" />
            <PortalProfileMenu />
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6">
          {organizationId && <Outlet />}
        </main>
        <Footer />
      </div>
    </div>
  );
}

function PortalNavContent() {
  return (
    <div className="flex h-full w-full flex-col gap-6 pt-4">
      <div className="flex items-center gap-2 px-4">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-black/5">
          <img src={logo} alt="ProjectPilot" className="h-full w-full object-cover object-left" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-extrabold tracking-tight text-gray-900 dark:text-gray-100">
            ProjectPilot AI
          </p>
          <p className="text-xs text-gray-400">Client Portal</p>
        </div>
      </div>
      {/* No horizontal padding on the scroll container itself — see Sidebar.tsx for why. */}
      <nav className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto pb-4 pl-4 pr-2">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              `flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-sm font-medium transition-colors ${
                isActive ? 'bg-brand-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
              }`
            }
          >
            <item.icon className="h-4 w-4 shrink-0" />
            {item.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
